import { makePersisted } from "@solid-primitives/storage";
import localforage from "localforage";
import { createContext, type ParentComponent, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";
import { z } from "zod";
import type { ProficiencyLevel } from "@/constants/languages";
import type {
  EducationInsertV2,
  PreviousJobInsertV2,
  ProfileV2,
} from "@/domains/freelance/profile/api/types";
import type { PickedSubcategory } from "@/domains/freelance/taxonomy/types";
import type {
  JobCategory,
  RemotePolicy,
} from "@/domains/job-offer/create/constants";
import {
  JobCategoryEnum,
  REMOTE_POLICY,
} from "@/domains/job-offer/create/constants";
import { devLog } from "@/libs/dev-log";
import { skillTermsSchema } from "@/libs/skill-input";
import {
  createCurrencyValidatorWithMinSchema,
  MIN_AMOUNT_EUR,
} from "@/libs/validators/currency";
import { createDefaultSector } from "../constants";
import { adultBirthdateSchema } from "../utils/birthdate";
import { onboardingStorePersistOptions } from "../utils/sanitize-onboarding-draft";

type OnboardingInitialState = {
  profile: ProfileV2;
};

export type OnboardingStep =
  | "source"
  | "sector"
  | "skills"
  | "job_title"
  | "experience"
  | "education"
  | "locale"
  | "summary"
  | "pricing"
  | "info"
  | "online-presence"
  | "review";

export type OnboardingExperience = Omit<
  PreviousJobInsertV2,
  "id" | "profile_id" | "start_date" | "end_date"
> & {
  temp_id: string;
  start_date: Date | null;
  end_date: Date | null;
};

export type OnboardingEducation = Omit<
  EducationInsertV2,
  "id" | "profile_id" | "created_at" | "updated_at" | "start_date" | "end_date"
> & {
  temp_id: string;
  start_date: Date | null;
  end_date: Date | null;
};

/**
 * The value of the `sector` step (freelancer-categories D5): the primary and
 * secondary category and the picked subcategories, each with where it came
 * from. The `POST /profile/onboarding` body sends the keys only (D6).
 */
export type SectorValue = {
  job_category: JobCategory | null;
  job_category_secondary: JobCategory | null;
  job_subcategories: PickedSubcategory[];
};

/**
 * The error codes of the `sector` step, rendered by the step with i18n keys:
 * `required` while the primary is missing, `sector_reset` when the draft had a
 * category outside the 8 and the sanitizer emptied it (AC16, AC20).
 */
export type SectorErrorCode = "required" | "sector_reset";

export interface OnboardingStore extends OnboardingInitialState {
  step: OnboardingStep;
  source: {
    value: "linkedin" | "resume" | "manual" | "linkedin_apify" | null;
    error: string | null;
    linkedin_sync_date?: Date | null;
    linkedin_url?: string | null;
  };
  sector: {
    value: SectorValue;
    /** Where the proposal came from, for the legend (FE10, FE11). */
    source: "cv" | "linkedin" | null;
    /** True once the freelancer has seen the sector (hub card, FE14). */
    reviewed: boolean;
    error: SectorErrorCode | null;
  };
  skills: {
    value: string[];
    error: string | null;
  };
  job_title: {
    value: string;
    error: string | null;
  };
  experience: {
    value: OnboardingExperience[];
    error: string | null;
  };
  education: {
    value: OnboardingEducation[];
    error: string | null;
  };
  locale: {
    value: {
      spoken_languages: {
        code: string;
        name: string;
        level: ProficiencyLevel;
      }[];
    };
    error: string | null;
  };
  summary: {
    value: string;
    error: string | null;
  };
  pricing: {
    value: {
      price_per_day: number | string;
    };
    error: string | null;
  };
  info: {
    value: Pick<ProfileV2, "avatar_url" | "residency" | "phone_number"> & {
      date_of_birth?: Date | null;
      resume?: File | null;
      is_available?: boolean;
      weekly_hours?: string;
      work_preference?: RemotePolicy;
    };
    error: string | null;
  };
  online_presence: {
    value: {
      website?: string;
      portfolio?: string;
      github?: string;
      linkedin?: string;
      instagram?: string;
    };
    error: string | null;
  };
}

const createOnboardingStore = (initialState: OnboardingInitialState) => {
  const DEFAULT_STATE: OnboardingStore = {
    step: "source",
    source: {
      value: null,
      error: null,
      linkedin_sync_date: null,
      linkedin_url: null,
    },
    sector: createDefaultSector(),
    skills: { value: [], error: null },
    job_title: { value: "", error: null },
    experience: { value: [], error: null },
    education: { value: [], error: null },
    locale: {
      value: {
        spoken_languages: [
          {
            code: "it",
            name: "Italian",
            level: "Madrelingua" as ProficiencyLevel,
          },
        ],
      },
      error: null,
    },
    summary: { value: "", error: null },
    pricing: { value: { price_per_day: "" }, error: null },
    info: {
      value: {
        avatar_url: initialState.profile.avatar_url,
        residency: initialState.profile.residency,
        resume: undefined as File | undefined,
        date_of_birth: null,
        is_available: true,
        weekly_hours: "40",
        work_preference: REMOTE_POLICY[0],
        phone_number: initialState.profile.phone_number ?? "",
      },
      error: null,
    },
    online_presence: {
      value: {
        website: initialState.profile.website ?? "",
        portfolio: initialState.profile.portfolio ?? "",
        github: initialState.profile.social_media?.github ?? "",
        linkedin: initialState.profile.social_media?.linkedin ?? "",
        instagram: initialState.profile.social_media?.instagram ?? "",
      },
      error: null,
    },
    profile: initialState.profile,
  };
  return makePersisted(
    createStore<OnboardingStore>({
      ...DEFAULT_STATE,
      ...initialState,
    }),
    onboardingStorePersistOptions(isServer ? undefined : localforage),
  );
};

const OnboardingContext =
  createContext<ReturnType<typeof createOnboardingStore>>();

export const OnboardingProvider: ParentComponent<{
  profile: ProfileV2;
}> = (props) => {
  const store = createOnboardingStore({ profile: props.profile });
  return (
    <OnboardingContext.Provider value={store}>
      {props.children}
    </OnboardingContext.Provider>
  );
};

const { schema: hourlyRateSchema } =
  createCurrencyValidatorWithMinSchema(MIN_AMOUNT_EUR);

export const linkedinOnboardingValidationSchema = z.object({
  skills: skillTermsSchema.min(1),
  job_category: z.enum(JobCategoryEnum),
  pricing: z.object({
    price_per_day: hourlyRateSchema,
  }),
  info: z.object({
    residency: z.string().min(1),
    date_of_birth: adultBirthdateSchema,
    resume: z.instanceof(File),
  }),
  online_presence: z.object({
    website: z.string().optional(),
    portfolio: z.string().optional(),
  }),
});

// Individual section validation schemas
const skillsValidationSchema = z.object({
  skills: skillTermsSchema.min(1),
});

const pricingValidationSchema = z.object({
  price_per_day: hourlyRateSchema,
});

const personalValidationSchema = z.object({
  residency: z.string().min(1),
  date_of_birth: z.date(),
});

const curriculumValidationSchema = z.object({
  resume: z.instanceof(File),
});

/**
 * «Avanti» of the LinkedIn hub. Besides the schema, the sector must have been
 * saved once from its card (`sector.reviewed`, AC24): a LinkedIn proposal is
 * never taken unseen.
 */
export const isLinkedInOnboardingComplete = (
  store: OnboardingStore,
): boolean => {
  if (!store.sector.reviewed) return false;
  // Extract and transform store values to match schema format
  const dateOfBirth = store.info.value.date_of_birth
    ? new Date(store.info.value.date_of_birth)
    : null;

  const data = {
    skills: store.skills.value,
    job_category: store.sector.value.job_category,
    pricing: {
      price_per_day: store.pricing.value.price_per_day,
    },
    info: {
      residency: store.info.value.residency,
      date_of_birth: dateOfBirth,
      resume: store.info.value.resume,
    },
    online_presence: {
      website: store.online_presence.value.website,
      portfolio: store.online_presence.value.portfolio,
    },
  };

  // Validate using the schema
  const result = linkedinOnboardingValidationSchema.safeParse(data);
  if (!result.success) {
    devLog(result.error);
  }
  devLog(result);
  return result.success;
};

// Individual section validation functions
/** The hub's «Competenze» card: the sector has its own card (AC23). */
export const isSkillsSectionComplete = (store: OnboardingStore): boolean => {
  const data = {
    skills: store.skills.value,
  };
  const result = skillsValidationSchema.safeParse(data);
  return result.success;
};

export const isPricingSectionComplete = (store: OnboardingStore): boolean => {
  const data = {
    price_per_day: store.pricing.value.price_per_day,
  };
  const result = pricingValidationSchema.safeParse(data);
  return result.success;
};

export const isPersonalSectionComplete = (store: OnboardingStore): boolean => {
  const dateOfBirth = store.info.value.date_of_birth
    ? store.info.value.date_of_birth
    : null;

  const data = {
    residency: store.info.value.residency,
    date_of_birth: dateOfBirth,
  };
  const result = personalValidationSchema.safeParse(data);
  return result.success;
};

export const isCurriculumSectionComplete = (
  store: OnboardingStore,
): boolean => {
  const data = {
    resume: store.info.value.resume,
  };
  const result = curriculumValidationSchema.safeParse(data);
  return result.success;
};

export const useOnboardingStore = () => {
  const store = useContext(OnboardingContext);
  if (!store) {
    throw new Error(
      "useOnboardingStore must be used within a OnboardingProvider",
    );
  }
  return store;
};

export const clearOnboardingStore = async () => {
  await localforage.removeItem("onboarding_store");
};
